import { useCallback, useEffect, useState, useRef } from "react";
import { api, fileUrl, formatApiErrorDetail } from "../lib/api";
import { PageHeader, Empty, Modal, Chip, EmbeddedActionBar } from "../components/ui-bits";
import { Plus, Search, Trash2, Edit3, Download, Upload, FileText, ChevronDown, ChevronRight, X as XIcon, GitBranch, FileSpreadsheet } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useBrandPermissions } from "../lib/brand";
import { useT } from "../lib/i18n";
import { useAutoOpenFromQuery } from "../lib/useAutoOpenFromQuery";

const emptyForm = {
  name: "", code: "", category: "", gender: "", season: "", year: null,
  collection_id: "",
  sizes: [], colors: [], materials: [], trims: [], measurements: [],
  size_measurements: null,
  techpack_draft: null,
  technical_notes: "", seamstress_notes: "",
  dev_phase: "Concept",
  production_status: "concept", dev_status: "draft",
  cost: 0, retail_price: 0,
};
const DEV_PHASES = ["Concept", "Disegno tecnico", "Cartamodello", "Primo campione",
                    "Revisione", "Approvato", "Produzione", "Archiviato"];
const PHASE_STYLE = {
  "Concept":         { bg: "rgba(115,115,115,.16)", fg: "var(--text-primary)" },
  "Disegno tecnico": { bg: "rgba(37,99,235,.16)",   fg: "#1D4ED8" },
  "Cartamodello":    { bg: "rgba(93,60,180,.16)",   fg: "#5D3CB4" },
  "Primo campione":  { bg: "rgba(217,119,6,.18)",   fg: "#A45A05" },
  "Revisione":       { bg: "rgba(217,119,6,.18)",   fg: "#A45A05" },
  "Approvato":       { bg: "rgba(15,133,84,.16)",   fg: "#0F8554" },
  "Produzione":      { bg: "rgba(15,118,110,.18)",  fg: "#0F766E" },
  "Archiviato":      { bg: "rgba(115,115,115,.14)", fg: "var(--text-secondary)" },
};
function PhaseChip({ value }) {
  const s = PHASE_STYLE[value] || PHASE_STYLE["Concept"];
  return (
    <span className="chip" style={{ background: s.bg, color: s.fg, borderColor: s.bg }}>
      <span className="w-1.5 h-1.5 rounded-full inline-block mr-1.5" style={{ background: s.fg }}/>
      {value || "—"}
    </span>
  );
}
const STATUSES = ["concept","sampling","production","shipped"];
const DEV_STATUSES = ["draft","approved","in_sampling","in_production","completed"];
const GENDERS = ["men","women","unisex","kids"];
const CATEGORIES = ["Jackets","Coats","Shirts","T-shirts","Knitwear","Pants","Shorts","Skirts","Dresses","Accessories","Dust bags","Packaging"];
const DEFAULT_SIZE_COLS = ["XS","S","M","L","XL","XXL"];
const DEFAULT_MEASUREMENT_LABELS = [
  "Chest","Shoulder","Body length","Sleeve length","Waist","Hip",
  "Bottom opening","Inseam","Thigh","Neck width","Armhole","Cuff opening",
];
const ACCEPT = ".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png";
const FILE_KINDS_GARMENT = [
  { key: "garment_pdf", label: "PDF attachment" },
  { key: "garment_technical", label: "Technical document" },
  { key: "garment_fabric", label: "Fabric file" },
  { key: "garment_drawing", label: "Technical drawing" },
  { key: "garment_mockup", label: "AI mockup" },
];

const csv = (s) => (s || "").split(",").map(x => x.trim()).filter(Boolean);

export default function GarmentsPage({ embedded = false } = {}) {
  const { canEdit, canAdmin } = useBrandPermissions();
  const t = useT();
  const [list, setList] = useState([]);
  const [collections, setCollections] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [search, setSearch] = useState("");
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editId, setEditId] = useState(null);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState({ collection_id: "", category: "", dev_phase: "" });
  const [detail, setDetail] = useState(null);
  const [sections, setSections] = useState({ main: true, variants: true, measurements: false, files: false });
  const fileRef = useRef();
  const pendingRef = useRef();
  const [pending, setPending] = useState([]);
  const addPending = (e) => {
    const ok = [...(e.target.files || [])].filter(f => /\.(pdf|jpe?g|png)$/i.test(f.name));
    if (ok.length < (e.target.files?.length || 0)) setError("Sono ammessi solo file PDF, JPG e PNG");
    setPending(p => [...p, ...ok]);
    e.target.value = "";
  };

  const load = useCallback(() => api.get("/garments", { params: {
    search: search || undefined,
    collection_id: filter.collection_id || undefined,
    category: filter.category || undefined,
    dev_phase: filter.dev_phase || undefined,
  }}).then(r => setList(r.data)), [search, filter]);

  useEffect(() => {
    load();
    api.get("/collections").then(r => setCollections(r.data));
    if (canEdit) api.get("/materials").then(r => setMaterials(r.data));
  }, [load, canEdit]);

  const open = (row) => {
    if (row) {
      setForm({ ...emptyForm, ...row, sizes: row.sizes || [], colors: row.colors || [], trims: row.trims || [] });
      setEditId(row.id);
    } else {
      setForm(emptyForm); setEditId(null);
    }
    setPending([]);
    setError(""); setModal(true);
  };
  useAutoOpenFromQuery(list, open);
  const navigate = useNavigate();
  const location = useLocation();
  useEffect(() => {
    const sec = new URLSearchParams(location.search).get("section");
    if (!modal || !sec || !["main", "variants", "measurements", "files"].includes(sec)) return;
    setSections(s => ({ ...s, [sec]: true }));
    setTimeout(() => document.querySelector(`[data-testid="garment-section-${sec}"]`)?.scrollIntoView({ behavior: "smooth", block: "start" }), 300);
  }, [modal, location.search]);

  // Auto-inherit season/year when a collection is picked.
  const setCollection = (collId) => {
    const c = collections.find(x => x.id === collId);
    setForm(f => ({
      ...f,
      collection_id: collId,
      ...(c ? {
        season: f.season || c.season || "",
        year: f.year || c.year || null,
      } : {}),
    }));
  };

  const save = async (e) => {
    e.preventDefault(); setError("");
    if (!form.code?.trim()) { setError(t("garment.err.code_required")); return; }
    try {
      const payload = {
        ...form,
        cost: Number(form.cost)||0, retail_price: Number(form.retail_price)||0,
        year: form.year ? Number(form.year) : null,
        sizes: Array.isArray(form.sizes) ? form.sizes : (form.sizes||"").split(",").map(x=>x.trim()).filter(Boolean),
        colors: Array.isArray(form.colors) ? form.colors : (form.colors||"").split(",").map(x=>x.trim()).filter(Boolean),
        trims: Array.isArray(form.trims) ? form.trims : (form.trims||"").split(",").map(x=>x.trim()).filter(Boolean),
      };
      if (editId) await api.put(`/garments/${editId}`, payload);
      else {
        const r = await api.post("/garments", payload);
        setEditId(r.data.id);
        for (const f of pending) {
          const fd = new FormData(); fd.append("file", f);
          fd.append("linked_type", f.type.startsWith("image/") ? "garment_image" : "garment_pdf"); fd.append("linked_id", r.data.id);
          await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" }});
        }
        setPending([]);
      }
      setModal(false); load();
      const ret = new URLSearchParams(location.search).get("return");
      if (ret && ret.startsWith("/garments/")) navigate(ret);
    } catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const remove = async (id) => { if (!window.confirm("Delete?")) return; await api.delete(`/garments/${id}`); load(); };

  const downloadPdf = async (id, code) => {
    const r = await api.get(`/pdf/techpack/${id}`, { responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a"); a.href = url; a.download = `techpack-${code}.pdf`; a.click();
    URL.revokeObjectURL(url);
  };

  const addMeasurement = () => setForm({ ...form, measurements: [...(form.measurements||[]), { label: "", value: "" }] });
  const updateMeasurement = (i, k, v) => {
    const arr = [...form.measurements]; arr[i] = { ...arr[i], [k]: v };
    setForm({ ...form, measurements: arr });
  };
  const removeMeasurement = (i) => setForm({ ...form, measurements: form.measurements.filter((_,j)=>j!==i) });

  // Size measurement table helpers
  const smt = form.size_measurements || { unit: "cm", base_size: "M", grading_notes: "", columns: DEFAULT_SIZE_COLS, rows: [] };
  const setSmt = (next) => setForm({ ...form, size_measurements: next });
  const seedSmt = () => {
    if ((smt.rows||[]).length > 0) return;
    setSmt({
      unit: smt.unit || "cm",
      base_size: smt.base_size || "M",
      grading_notes: smt.grading_notes || "",
      columns: smt.columns?.length ? smt.columns : DEFAULT_SIZE_COLS,
      rows: DEFAULT_MEASUREMENT_LABELS.map(l => ({ label: l, values: {}, tolerance: "" })),
    });
  };
  const smtAddRow = () => setSmt({ ...smt, columns: smt.columns?.length ? smt.columns : DEFAULT_SIZE_COLS, rows: [...(smt.rows||[]), { label: "", values: {}, tolerance: "" }] });
  const smtRemoveRow = (i) => setSmt({ ...smt, rows: (smt.rows||[]).filter((_,j)=>j!==i) });
  const smtUpdateRow = (i, patch) => {
    const rows = [...(smt.rows||[])];
    rows[i] = { ...rows[i], ...patch };
    setSmt({ ...smt, rows });
  };
  const smtUpdateVal = (i, col, val) => {
    const rows = [...(smt.rows||[])];
    rows[i] = { ...rows[i], values: { ...(rows[i].values||{}), [col]: val } };
    setSmt({ ...smt, rows });
  };

  const addMaterial = () => setForm({ ...form, materials: [...(form.materials||[]), { material_id: "", quantity: 1, notes: "" }] });
  const updateMaterial = (i, k, v) => {
    const arr = [...form.materials]; arr[i] = { ...arr[i], [k]: v };
    setForm({ ...form, materials: arr });
  };
  const removeMaterial = (i) => setForm({ ...form, materials: form.materials.filter((_,j)=>j!==i) });

  const uploadImage = async (e) => {
    const file = e.target.files?.[0]; if (!file || !editId) return;
    const fd = new FormData(); fd.append("file", file);
    fd.append("linked_type", "garment_image"); fd.append("linked_id", editId);
    const r = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" }});
    // reload the garment to get updated images array + main_image_id
    const gr = await api.get(`/garments/${editId}`);
    setForm({ ...form, images: gr.data.images, main_image_id: gr.data.main_image_id });
    load();
  };

  const [fileKind, setFileKind] = useState("garment_pdf");
  const [garmentFiles, setGarmentFiles] = useState([]);
  const fileDocRef = useRef();

  const loadGarmentFiles = async (gid) => {
    if (!gid) { setGarmentFiles([]); return; }
    const kinds = FILE_KINDS_GARMENT.map(k=>k.key);
    const promises = kinds.map(k => api.get("/files", { params: { linked_type: k, linked_id: gid }}).then(r=>r.data));
    const all = (await Promise.all(promises)).flat();
    setGarmentFiles(all);
  };
  useEffect(() => { loadGarmentFiles(editId); }, [editId, modal]);

  const uploadDoc = async (e) => {
    const file = e.target.files?.[0]; if (!file || !editId) return;
    const fd = new FormData();
    fd.append("file", file);
    fd.append("linked_type", fileKind);
    fd.append("linked_id", editId);
    await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" }});
    loadGarmentFiles(editId);
    e.target.value = "";
  };

  const removeDoc = async (fid) => {
    if (!window.confirm("Delete this file?")) return;
    await api.delete(`/files/${fid}`);
    loadGarmentFiles(editId);
    const gr = await api.get(`/garments/${editId}`);
    setForm({ ...form, images: gr.data.images, main_image_id: gr.data.main_image_id });
  };

  const setMainImage = async (fid) => {
    await api.patch(`/garments/${editId}/main-image`, null, { params: { file_id: fid }});
    setForm({ ...form, main_image_id: fid });
    load();
  };

  const removeImage = async (fid) => {
    if (!window.confirm("Delete this image?")) return;
    await api.delete(`/files/${fid}`);
    const gr = await api.get(`/garments/${editId}`);
    setForm({ ...form, images: gr.data.images, main_image_id: gr.data.main_image_id });
    load();
  };

  const imgUrl = (id) => fileUrl(id);

  return (
    <div data-testid="garments-page">
      {!embedded && (
      <PageHeader eyebrow="Catalogue" title="Garments"
        action={canEdit && (
          <div className="flex gap-2">
            <Link to="/garments/import" data-testid="import-excel-btn" className="btn-ghost inline-flex items-center gap-2"><FileSpreadsheet size={14}/> Importa da Excel</Link>
            <button data-testid="new-garment-btn" onClick={()=>open(null)} className="btn-gold inline-flex items-center gap-2"><Plus size={14}/>New Garment</button>
          </div>
        )}/>
      )}

      {embedded && canEdit && (
        <EmbeddedActionBar action={
          <div className="flex gap-2">
            <Link to="/garments/import" data-testid="import-excel-btn" className="btn-ghost inline-flex items-center gap-2">
              <FileSpreadsheet size={14}/> Importa da Excel
            </Link>
            <button data-testid="new-garment-btn" onClick={()=>open(null)} className="btn-gold inline-flex items-center gap-2">
              <Plus size={14}/> {t("garments.new")}
            </button>
          </div>
        }/>
      )}

      <div className="flex flex-wrap gap-3 mb-4">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--text-muted,#737373)]"/>
          <input data-testid="garments-search" placeholder={t("common.search")} value={search} onChange={(e)=>setSearch(e.target.value)} className="input-dark pl-9"/>
        </div>
        <select data-testid="garments-filter-collection" className="input-dark max-w-xs" value={filter.collection_id} onChange={(e)=>setFilter({...filter, collection_id: e.target.value})}>
          <option value="">{t("garment.filter.all_collections")}</option>
          {collections.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <select data-testid="garments-filter-category" className="input-dark max-w-xs" value={filter.category} onChange={(e)=>setFilter({...filter, category: e.target.value})}>
          <option value="">{t("garment.filter.all_categories")}</option>
          {CATEGORIES.map(c => <option key={c}>{c}</option>)}
        </select>
        <select data-testid="garments-filter-phase" className="input-dark max-w-xs" value={filter.dev_phase} onChange={(e)=>setFilter({...filter, dev_phase: e.target.value})}>
          <option value="">{t("garment.filter.all_phases")}</option>
          {DEV_PHASES.map(p => <option key={p}>{p}</option>)}
        </select>
      </div>

      {list.length === 0 ? (
        <Empty
          title={t("garments.empty")}
          cta={canEdit ? { label: t("garments.cta_first"), onClick: ()=>open(null) } : null}
          ctaTestId="garments-empty-cta"
        />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {list.map(g => (
            <div key={g.id} className="card-dark hover-lift overflow-hidden" data-testid={`garment-card-${g.id}`}>
              <div className="aspect-[4/5] bg-[#FAFAFA] relative overflow-hidden">
                {g.main_image_id ? (
                  <img src={imgUrl(g.main_image_id)} alt={g.name} className="w-full h-full object-cover"/>
                ) : g.images?.[0] ? (
                  <img src={imgUrl(g.images[0])} alt={g.name} className="w-full h-full object-cover"/>
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <div className="label-caps text-[#D4D4D4]">No image</div>
                  </div>
                )}
                <div className="absolute top-3 right-3"><PhaseChip value={g.dev_phase || "Concept"}/></div>
              </div>
              <div className="p-5">
                <div className="label-caps text-[#0A0A0A] mb-1">{g.code}</div>
                <h3 className="font-serif text-xl leading-tight">{g.name}</h3>
                <div className="text-xs text-[#525252] mt-1">{g.category || "—"}</div>
                <div className="flex items-center justify-between mt-4">
                  <div className="text-sm">€ {(g.retail_price||0).toFixed(2)}</div>
                  <div className="flex gap-2">
                    <button data-testid={`garment-techpack-${g.id}`} onClick={()=>downloadPdf(g.id, g.code)} className="text-[#525252] hover:text-[#0A0A0A]" title="Tech Pack PDF"><FileText size={14}/></button>
                    <button data-testid={`garment-development-${g.id}`} onClick={()=>navigate(`/garments/${g.id}/development`)} className="text-[#525252] hover:text-[#0A0A0A]" title="Sviluppo campionario"><GitBranch size={14}/></button>
                    {canEdit && <>
                      <button data-testid={`garment-edit-${g.id}`} onClick={()=>open(g)} className="text-[#525252] hover:text-[#0A0A0A]"><Edit3 size={14}/></button>
                      {canAdmin && <button data-testid={`garment-delete-${g.id}`} onClick={()=>remove(g.id)} className="text-[#525252] hover:text-red-600"><Trash2 size={14}/></button>}
                    </>}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal open={modal} onClose={()=>{
        setModal(false);
        const ret = new URLSearchParams(location.search).get("return");
        if (ret && ret.startsWith("/garments/")) navigate(ret);
      }} title={editId ? "Modifica capo" : "Nuovo capo"} wide>
        <form onSubmit={save} className="flex flex-col" style={{ maxHeight: "calc(90vh - 120px)" }}>
          {/* Sticky wrapper: the header is provided by <Modal>; footer stays sticky below */}
          <div className="overflow-y-auto pr-1" style={{ flex: 1 }} data-testid="garment-form-scroll">
            {/* ================= Section 1: main info ================= */}
            <Section
              open={sections.main}
              onToggle={()=>setSections(s=>({...s, main: !s.main}))}
              title={t("garment.section.main")}
              testId="garment-section-main"
            >
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div><label className="label-caps block mb-2">{t("garment.field.code")} *</label>
                  <input data-testid="garment-form-code" required className="input-dark" value={form.code} onChange={(e)=>setForm({...form,code:e.target.value})}/></div>
                <div><label className="label-caps block mb-2">{t("garment.field.name")} *</label>
                  <input data-testid="garment-form-name" required className="input-dark" value={form.name} onChange={(e)=>setForm({...form,name:e.target.value})}/></div>
                <div><label className="label-caps block mb-2">{t("garment.field.category")}</label>
                  <select className="input-dark" value={form.category||""} onChange={(e)=>setForm({...form,category:e.target.value})}>
                    <option value="">—</option>{CATEGORIES.map(c=><option key={c}>{c}</option>)}
                  </select></div>
                <div><label className="label-caps block mb-2">{t("garment.field.gender")}</label>
                  <select data-testid="garment-form-gender" className="input-dark" value={form.gender||""} onChange={(e)=>setForm({...form,gender:e.target.value})}>
                    <option value="">—</option>{GENDERS.map(g=><option key={g}>{g}</option>)}
                  </select></div>
                <div><label className="label-caps block mb-2">{t("garment.field.season")}</label>
                  <input className="input-dark" value={form.season||""} onChange={(e)=>setForm({...form,season:e.target.value})}/></div>
                <div><label className="label-caps block mb-2">{t("garment.field.year")}</label>
                  <input type="number" className="input-dark" value={form.year||""} onChange={(e)=>setForm({...form,year:e.target.value?Number(e.target.value):null})}/></div>
                <div><label className="label-caps block mb-2">{t("garment.field.collection")}</label>
                  <select data-testid="garment-form-collection" className="input-dark" value={form.collection_id||""} onChange={(e)=>setCollection(e.target.value)}>
                    <option value="">—</option>{collections.map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
                  </select></div>
                <div><label className="label-caps block mb-2">{t("garment.field.dev_phase")}</label>
                  <select data-testid="garment-form-dev-phase" className="input-dark" value={form.dev_phase}
                    disabled={!!form.sample_stage && form.sample_stage !== "development"}
                    onChange={(e)=>setForm({...form,dev_phase:e.target.value})}>
                    {DEV_PHASES.filter(p => p === form.dev_phase || !["Approvato","Produzione"].includes(p)).map(p=><option key={p}>{p}</option>)}
                  </select>
                  {form.sample_stage && form.sample_stage !== "development"
                    ? <div className="text-xs opacity-60 mt-1">Gestita dalla timeline di sviluppo</div>
                    : <div className="text-xs opacity-60 mt-1">Approvazione e produzione solo dalla pagina Sviluppo</div>}</div>
                <div><label className="label-caps block mb-2">{t("garment.field.cost")}</label>
                  <input type="number" step="0.01" className="input-dark" value={form.cost} onChange={(e)=>setForm({...form,cost:e.target.value})}/></div>
                <div><label className="label-caps block mb-2">{t("garment.field.retail")}</label>
                  <input type="number" step="0.01" className="input-dark" value={form.retail_price} onChange={(e)=>setForm({...form,retail_price:e.target.value})}/></div>
              </div>
            </Section>

            {/* ================= Section 2: variants & materials ================= */}
            <Section
              open={sections.variants}
              onToggle={()=>setSections(s=>({...s, variants: !s.variants}))}
              title={t("garment.section.variants")}
              testId="garment-section-variants"
            >
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <div><label className="label-caps block mb-2">{t("garment.field.sizes")}</label>
                  <ChipInput value={form.sizes} onChange={(v)=>setForm({...form,sizes:v})} testId="garment-form-sizes" placeholder={t("garment.chip.add")}/></div>
                <div><label className="label-caps block mb-2">{t("garment.field.colors")}</label>
                  <ChipInput value={form.colors} onChange={(v)=>setForm({...form,colors:v})} testId="garment-form-colors" placeholder={t("garment.chip.add")}/></div>
                <div><label className="label-caps block mb-2">{t("garment.field.trims")}</label>
                  <ChipInput value={form.trims} onChange={(v)=>setForm({...form,trims:v})} testId="garment-form-trims" placeholder={t("garment.chip.add")}/></div>
              </div>

              <div className="flex items-center justify-between mb-2">
                <label className="label-caps">{t("garment.field.materials")}</label>
                <button type="button" onClick={addMaterial} className="text-xs text-[color:var(--text-primary)]" data-testid="garment-add-material">+ {t("garment.add_material")}</button>
              </div>
              {(form.materials||[]).map((m,i)=>(
                <div key={i} className="grid grid-cols-12 gap-2 mb-2">
                  <select className="input-dark col-span-5" value={m.material_id} onChange={(e)=>updateMaterial(i,"material_id",e.target.value)} data-testid={`garment-material-${i}`}>
                    <option value="">— materiale —</option>{materials.map(x=><option key={x.id} value={x.id}>{x.name} {x.unit?`(${x.unit})`:""}</option>)}
                  </select>
                  <input type="number" step="0.01" className="input-dark col-span-2" value={m.quantity} onChange={(e)=>updateMaterial(i,"quantity",Number(e.target.value)||0)} placeholder="Q.tà"/>
                  <input className="input-dark col-span-4" value={m.notes||""} onChange={(e)=>updateMaterial(i,"notes",e.target.value)} placeholder="Note (consumo, ecc.)"/>
                  <button type="button" onClick={()=>removeMaterial(i)} className="col-span-1 text-red-600"><Trash2 size={14}/></button>
                </div>
              ))}
            </Section>

            {/* ================= Section 3: measurements + grading ================= */}
            <Section
              open={sections.measurements}
              onToggle={()=>setSections(s=>({...s, measurements: !s.measurements}))}
              title={t("garment.section.measurements")}
              testId="garment-section-measurements"
            >
              <div className="flex items-center justify-between mb-2">
                <label className="label-caps">Misure di base</label>
                <button type="button" onClick={addMeasurement} className="text-xs text-[color:var(--text-primary)]">+ {t("garment.add_line")}</button>
              </div>
              {(form.measurements||[]).map((m,i)=>(
                <div key={i} className="grid grid-cols-12 gap-2 mb-2">
                  <input className="input-dark col-span-5" value={m.label} onChange={(e)=>updateMeasurement(i,"label",e.target.value)} placeholder="Petto"/>
                  <input className="input-dark col-span-6" value={m.value} onChange={(e)=>updateMeasurement(i,"value",e.target.value)} placeholder="98 cm"/>
                  <button type="button" onClick={()=>removeMeasurement(i)} className="col-span-1 text-red-600"><Trash2 size={14}/></button>
                </div>
              ))}

              <div className="border-t border-[color:var(--border,#E5E5E5)] pt-4 mt-4" data-testid="size-measurements-section">
                <div className="flex flex-wrap items-center justify-between mb-3 gap-2">
                  <label className="label-caps">Tabella misure e grading</label>
                  <div className="flex items-center gap-2 flex-wrap">
                    <select className="input-dark py-1 text-xs" value={smt.unit || "cm"} onChange={(e)=>setSmt({...smt, unit: e.target.value})} data-testid="smt-unit">
                      <option value="cm">cm</option>
                      <option value="in">inch</option>
                    </select>
                    <input className="input-dark py-1 text-xs w-24" placeholder="Taglia base" value={smt.base_size || ""} onChange={(e)=>setSmt({...smt, base_size: e.target.value})}/>
                    <button type="button" onClick={seedSmt} className="text-xs text-[color:var(--text-primary)]" data-testid="smt-seed">Precompila righe standard</button>
                    <button type="button" onClick={smtAddRow} className="text-xs text-[color:var(--text-primary)]" data-testid="smt-add-row">+ Riga</button>
                  </div>
                </div>
                {(smt.rows||[]).length === 0 ? (
                  <div className="text-xs text-[color:var(--text-muted,#737373)] italic">Nessuna riga — usa &quot;Precompila righe standard&quot;.</div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs" data-testid="smt-table">
                      <thead>
                        <tr className="border-b border-[color:var(--border,#E5E5E5)] text-[color:var(--text-secondary,#525252)] uppercase tracking-widest">
                          <th className="text-left py-2 pr-2">Misura</th>
                          {(smt.columns||DEFAULT_SIZE_COLS).map(c => <th key={c} className="text-right px-1 min-w-[52px]">{c}</th>)}
                          <th className="text-right px-1">Tol.</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {(smt.rows||[]).map((row, i) => (
                          <tr key={i} className="border-b border-[color:var(--border,#E5E5E5)]/40">
                            <td className="py-1 pr-2"><input className="input-dark py-1 text-xs" value={row.label} onChange={(e)=>smtUpdateRow(i, { label: e.target.value })} placeholder="Petto" data-testid={`smt-row-label-${i}`}/></td>
                            {(smt.columns||DEFAULT_SIZE_COLS).map(col => (
                              <td key={col} className="px-1">
                                <input className="input-dark py-1 text-xs text-right" value={row.values?.[col] || ""} onChange={(e)=>smtUpdateVal(i, col, e.target.value)} data-testid={`smt-row-${i}-${col}`}/>
                              </td>
                            ))}
                            <td className="px-1"><input className="input-dark py-1 text-xs text-right w-16" placeholder="±" value={row.tolerance||""} onChange={(e)=>smtUpdateRow(i, { tolerance: e.target.value })}/></td>
                            <td className="pl-1"><button type="button" onClick={()=>smtRemoveRow(i)} className="text-red-600"><Trash2 size={12}/></button></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                <div className="mt-3">
                  <label className="label-caps block mb-1">Note grading (facoltative)</label>
                  <input className="input-dark text-xs" value={smt.grading_notes || ""} onChange={(e)=>setSmt({...smt, grading_notes: e.target.value})} placeholder="es. 2cm di grade tra le taglie"/>
                </div>
              </div>
            </Section>

            {/* ================= Section 4: files + notes ================= */}
            <Section
              open={sections.files}
              onToggle={()=>setSections(s=>({...s, files: !s.files}))}
              title={t("garment.section.files")}
              testId="garment-section-files"
            >
              <div className="mb-4">
                <label className="label-caps block mb-2">{t("garment.field.tech_notes")}</label>
                <textarea rows={3} className="input-dark" value={form.technical_notes||""} onChange={(e)=>setForm({...form,technical_notes:e.target.value})}/>
              </div>
              <div className="mb-4">
                <label className="label-caps block mb-2">{t("garment.field.seamstress_notes")}</label>
                <textarea rows={3} className="input-dark" placeholder="Istruzioni interne, tolleranze, rifiniture..." value={form.seamstress_notes||""} onChange={(e)=>setForm({...form,seamstress_notes:e.target.value})} data-testid="garment-seamstress-notes"/>
              </div>

              {!editId && (
                <div className="mb-4" data-testid="garment-pending-files">
                  <label className="label-caps block mb-2">Foto e documenti (PDF, JPG, PNG)</label>
                  <div className="flex flex-wrap items-center gap-3">
                    {pending.map((f, i) => (
                      <div key={i} className="card-dark p-2 flex items-center gap-2 text-xs" data-testid={`garment-pending-${i}`}>
                        {f.type.startsWith("image/") ? <img src={URL.createObjectURL(f)} alt="" className="w-10 h-12 object-cover"/> : <FileText size={20} strokeWidth={1}/>}
                        <span className="max-w-[160px] truncate">{f.name}</span>
                        <button type="button" onClick={()=>setPending(p=>p.filter((_,j)=>j!==i))} className="hover:text-red-600" data-testid={`garment-pending-remove-${i}`}><Trash2 size={12}/></button>
                      </div>
                    ))}
                    <button type="button" onClick={()=>pendingRef.current?.click()} className="btn-ghost inline-flex items-center gap-2" data-testid="garment-attach-new">
                      <Upload size={12}/> Allega file
                    </button>
                    <input ref={pendingRef} type="file" multiple accept={ACCEPT} hidden onChange={addPending} data-testid="garment-attach-input"/>
                  </div>
                  <div className="text-xs mt-2 opacity-60">Le foto diventano immagini del capo (la prima è la principale), i PDF vanno nei documenti. Vengono caricati quando salvi.</div>
                </div>
              )}

              {editId && (
                <div className="mb-4">
                  <label className="label-caps block mb-2">Immagini (clic per impostare come principale)</label>
                  <div className="flex flex-wrap gap-3">
                    {(form.images||[]).map(id => (
                      <div key={id} className={`relative group border ${form.main_image_id === id ? "border-[color:var(--text-primary,#0A0A0A)]" : "border-[color:var(--border,#E5E5E5)]"}`}>
                        <img src={imgUrl(id)} alt="" className="w-20 h-24 object-cover cursor-pointer" onClick={()=>setMainImage(id)}/>
                        {form.main_image_id === id && (<div className="absolute top-1 left-1 text-[9px] uppercase tracking-widest bg-white/80 px-1">Principale</div>)}
                        <button type="button" onClick={()=>removeImage(id)} className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 bg-black/60 p-1 text-red-500"><Trash2 size={10}/></button>
                      </div>
                    ))}
                    <button type="button" onClick={()=>fileRef.current?.click()} className="dropzone w-20 h-24 flex items-center justify-center text-[color:var(--text-muted,#737373)]" data-testid="garment-upload-image">
                      <Upload size={16}/>
                    </button>
                    <input ref={fileRef} type="file" accept=".jpg,.jpeg,.png,image/jpeg,image/png" hidden onChange={uploadImage}/>
                  </div>
                </div>
              )}

              {editId && (
                <div className="border-t border-[color:var(--border,#E5E5E5)] pt-4">
                  <div className="flex items-center gap-3 mb-3 flex-wrap">
                    <label className="label-caps">Documenti e disegni tecnici</label>
                    <select data-testid="garment-file-kind" className="input-dark max-w-xs" value={fileKind} onChange={(e)=>setFileKind(e.target.value)}>
                      {FILE_KINDS_GARMENT.map(k=><option key={k.key} value={k.key}>{k.label}</option>)}
                    </select>
                    <button type="button" onClick={()=>fileDocRef.current?.click()} className="btn-ghost inline-flex items-center gap-2" data-testid="garment-upload-file">
                      <Upload size={12}/> Carica
                    </button>
                    <input ref={fileDocRef} type="file" accept={ACCEPT} hidden onChange={uploadDoc}/>
                  </div>
                  {garmentFiles.length === 0 ? (
                    <div className="text-xs text-[color:var(--text-muted,#737373)]">Nessun documento.</div>
                  ) : (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {garmentFiles.map(f => (
                        <div key={f.id} className="card-dark p-3 flex items-center gap-3">
                          {(f.content_type || "").startsWith("image/") ? (
                            <img src={imgUrl(f.id)} alt="" className="w-10 h-12 object-cover"/>
                          ) : (
                            <FileText size={24} className="text-[color:var(--text-primary,#0A0A0A)]" strokeWidth={1}/>
                          )}
                          <div className="flex-1 min-w-0">
                            <div className="text-[10px] uppercase tracking-widest text-[color:var(--text-primary,#0A0A0A)]">{FILE_KINDS_GARMENT.find(k=>k.key===f.linked_type)?.label || f.linked_type}</div>
                            <div className="text-xs truncate">{f.original_filename}</div>
                          </div>
                          <a href={imgUrl(f.id)} target="_blank" rel="noreferrer" className="text-[color:var(--text-secondary,#525252)] hover:text-[color:var(--text-primary,#0A0A0A)]"><Download size={12}/></a>
                          <button type="button" onClick={()=>removeDoc(f.id)} className="text-[color:var(--text-secondary,#525252)] hover:text-red-600"><Trash2 size={12}/></button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </Section>
          </div>

          {/* Sticky footer */}
          <div className="sticky bottom-0 flex items-center gap-3 pt-4 border-t border-[color:var(--border,#E5E5E5)] bg-[color:var(--background,#fff)]" style={{ marginTop: 0 }}>
            {error && <div className="text-red-600 text-sm flex-1">{error}</div>}
            <div className="ml-auto flex gap-3">
              <button type="button" onClick={()=>setModal(false)} className="btn-ghost">{t("garment.form.close")}</button>
              <button data-testid="garment-form-save" className="btn-gold">{t("garment.form.save")}</button>
            </div>
          </div>
        </form>
      </Modal>
    </div>
  );
}

// -------- helpers --------
function Section({ title, open, onToggle, testId, children }) {
  return (
    <div className="border-b border-[color:var(--border,#E5E5E5)] py-4" data-testid={testId}>
      <button type="button" onClick={onToggle}
        className="w-full flex items-center justify-between text-left label-caps mb-3">
        <span>{title}</span>
        {open ? <ChevronDown size={14}/> : <ChevronRight size={14}/>}
      </button>
      {open && <div>{children}</div>}
    </div>
  );
}

function ChipInput({ value = [], onChange, placeholder, testId }) {
  const [text, setText] = useState("");
  const items = Array.isArray(value) ? value : [];
  const add = () => {
    const v = text.trim();
    if (!v) return;
    if (!items.includes(v)) onChange([...items, v]);
    setText("");
  };
  return (
    <div className="input-dark flex flex-wrap gap-1 items-center min-h-[38px]" data-testid={testId}>
      {items.map((v, i) => (
        <span key={i} className="chip inline-flex items-center gap-1">
          {v}
          <button type="button" onClick={()=>onChange(items.filter((_,j)=>j!==i))} className="text-[color:var(--text-muted,#737373)] hover:text-[color:var(--text-primary)]"><XIcon size={10}/></button>
        </span>
      ))}
      <input
        className="flex-1 min-w-[80px] bg-transparent outline-none text-sm"
        value={text}
        onChange={(e)=>setText(e.target.value)}
        onKeyDown={(e)=>{ if (e.key==="Enter" || e.key===",") { e.preventDefault(); add(); } }}
        onBlur={add}
        placeholder={placeholder}
      />
    </div>
  );
}
